<template>
    <div>
        <PageHeader :show-log-on-btn="false" />
        <div class="quote-page">
            <img
                v-for="product in products"
                v-show="activeProduct === product.name.toLowerCase()"
                :key="product.name"
                :data-src="product.img"
                :alt="`banner image for ${product.name}`"
                class="lazyload banner"
                width="1440"
                height="400"
            />

            <div class="blocks">
                <div class="quoter-box">
                    <h2>
                        Start a{{ activeProduct === 'auto' ? `n ` : `` }}
                        <span style="text-transform: capitalize">{{ activeProduct }}</span>
                        Quote
                    </h2>
                    <form class="quote-form" @submit.prevent="submitQuoteRequest()">
                        <div style="display: grid">
                            <label for="product-select">Select a Product</label>
                            <div class="select">
                                <select
                                    id="product-select"
                                    name="product-select"
                                    @change="(event) => setActiveProduct(event.target.value)"
                                >
                                    <option
                                        v-for="product in products"
                                        :key="product.name"
                                        :value="product.name.toLowerCase()"
                                        :selected="activeProduct === product.name.toLowerCase()"
                                    >
                                        {{ product.name }}
                                    </option>
                                </select>
                            </div>
                        </div>
                        <button
                            class="btn btn-gaq"
                            style="height: 50px; margin-top: 16px"
                            type="submit"
                        >
                            Start Quote
                        </button>
                    </form>
                </div>
                <div class="block profile">
                    <div class="agent-info">
                        <img
                            class="lazyload"
                            :alt="agent.names.PreferredName"
                            :data-src="agent.image.profile"
                        />
                        <div class="contact">
                            <a :href="`tel:${phone.stripped}`" class="icon-and-text">
                                <svgicon color="#00884e" height="20" icon="phone" />
                                {{ phone.pretty }}
                            </a>
                            <h1>{{ agent.names.PreferredName }}</h1>
                            <p>
                                <span style="font-size: 1.5rem">Desjardins Insurance Agent</span>
                                <span>
                                    {{ office.physicalAddress.street1 }}
                                    <br />
                                    <span v-if="office.physicalAddress.street2">
                                        {{ office.physicalAddress.street2 }}
                                        <br />
                                    </span>
                                    {{ office.physicalAddress.municipality }},
                                    {{ office.physicalAddress.provinceCode }}
                                    {{ office.physicalAddress.postalCode }}
                                </span>
                                <a :href="`https://${meta.domain}/`" class="icon-and-text">
                                    <svgicon :original="true" height="25" icon="click" />
                                    {{ meta.domain }}
                                </a>
                            </p>
                        </div>
                    </div>
                    <ValidationObserver v-slot="{ errors, handleSubmit, reset }" slim>
                        <form
                            ref="contactMe"
                            class="contact-form"
                            @submit.prevent="handleSubmit(sendMessage)"
                            @reset.prevent="reset"
                        >
                            <h4>Send a message to {{ agent.names.PreferredName.split(' ')[0] }}</h4>
                            <div class="name-and-phemail">
                                <ValidationProvider
                                    v-slot="{ ariaInput, errors, ariaMsg }"
                                    name="Your Name"
                                    rules="required"
                                    mode="eager"
                                    class="inputWrapper"
                                >
                                    <input
                                        v-model="contactForm.name"
                                        v-bind="ariaInput"
                                        aria-label="Your name"
                                        placeholder="Your Name"
                                        type="text"
                                        :class="{ hasError: errors.length > 0 }"
                                    />
                                    <span v-bind="ariaMsg">{{ errors[0] }}</span>
                                </ValidationProvider>
                                <ValidationProvider
                                    v-slot="{ ariaInput, errors, ariaMsg }"
                                    name="Contact Info"
                                    rules="required|contact"
                                    mode="eager"
                                    class="inputWrapper"
                                >
                                    <input
                                        v-model="contactForm.contact"
                                        v-bind="ariaInput"
                                        aria-label="Your email or phone"
                                        placeholder="Your Email or Phone"
                                        type="text"
                                        :class="{ hasError: errors.length > 0 }"
                                        class="pa11y-ignore"
                                    />
                                    <span v-bind="ariaMsg">{{ errors[0] }}</span>
                                </ValidationProvider>
                            </div>
                            <ValidationProvider
                                v-slot="{ ariaInput, errors, ariaMsg }"
                                name="Your Message"
                                rules="required"
                                mode="eager"
                                class="inputWrapper"
                            >
                                <textarea
                                    v-model="contactForm.message"
                                    v-bind="ariaInput"
                                    aria-label="message"
                                    placeholder="Your Message"
                                    :class="{ hasError: errors.length > 0 }"
                                    class="pa11y-ignore"
                                />
                                <span v-bind="ariaMsg">{{ errors[0] }}</span>
                            </ValidationProvider>
                            <button
                                :disabled="
                                    sending || (submit_attempted && !validated) || errors.length > 0
                                "
                                class="btn-green"
                                type="submit"
                            >
                                Send Message
                            </button>
                            <div v-if="send_response" aria-live="polite" class="form-response">
                                <h3 v-html="send_response" />
                            </div>
                        </form>
                    </ValidationObserver>
                </div>
            </div>
            <div style="margin: 0 2rem">
                <a
                    id="qlp-business-brochure"
                    href="https://docs.desjardins.com/dan/business-insurance/"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    <img
                        v-if="activeProduct === 'business'"
                        class="mobile-qlp-business-brochure-image lazyload"
                        alt="Desjardins business insurance – Insuring your business just got easier"
                        :data-src="businessBrochureMobile"
                    />
                    <img
                        v-if="activeProduct === 'business'"
                        class="desktop-qlp-business-brochure-image lazyload"
                        alt="Desjardins business insurance – Insuring your business just got easier"
                        :data-src="businessBrochureDesktop"
                    />
                </a>
            </div>
        </div>
    </div>
</template>
<script>
import _ from 'lodash';
import { mapGetters } from 'vuex';
import PageHeader from '@/components/Layout/PageHeader.vue';
import axios from 'axios';
import QuoteHelpers from '@/mixins/quote';
import Contact from '@/mixins/contact';
import autoBanner from '@/assets/img/auto.jpg';
import homeBanner from '@/assets/img/home.jpg';
import tenantsBanner from '@/assets/img/tenants.jpg';
import condoBanner from '@/assets/img/condo.jpg';
import businessBanner from '@/assets/img/business.jpg';
import businessBrochureMobile from '@/assets/img/business-brochure-350x170.jpg';
import businessBrochureDesktop from '@/assets/img/business-brochure-1110x180.jpg';
import { ValidationObserver, ValidationProvider } from 'vee-validate';

const imageMap = {
    au_qq: autoBanner,
    po: homeBanner,
    lo: tenantsBanner,
    co: condoBanner,
    de: businessBanner,
};

const nameMap = {
    'Condo Owners': 'Condo',
    Homeowners: 'Home',
};

export default {
    components: { PageHeader, ValidationObserver, ValidationProvider },
    mixins: [QuoteHelpers, Contact],
    data: () => ({
        activeProduct: 'auto',
        errors: [],
        sending: false,
        send_response: null,
        products: [],
        businessBrochureMobile,
        businessBrochureDesktop,
    }),
    computed: {
        ...mapGetters(['office']),
        currentImg() {
            return `@/assets/img/car.jpg`;
        },
    },
    mounted() {
        this.office.quoteOptions.forEach((quoteOption) => {
            const [name, code] = quoteOption;
            this.products.push({
                code: code,
                img: imageMap[code] || autoBanner,
                name: nameMap[name] || name,
            });
        });
        this.setActiveProduct(this.$route.params.product);
    },
    methods: {
        reset() {
            this.contactForm = {
                name: null,
                contact: null,
                message: null,
            };
            this.$refs.contactMe?.reset();
        },
        responseMessage(msg, reset) {
            this.send_response = msg;
            if (msg) {
                setTimeout(() => {
                    this.send_response = null;
                }, 6000);
            }

            if (reset) {
                this.sending = false;
                this.submit_attempted = false;
                this.reset();
            }
        },
        sendMessage() {
            this.submit_attempted = true;

            const data = { ..._.omit(this.contactForm, 'contact') };
            this.sending = true;
            this.send_response = 'Sending... please wait...';

            this.$keen('contactform', data);

            axios({
                method: 'post',
                url: '/contact',
                data,
            })
                .then((response) => {
                    if (response.data.success) {
                        this.responseMessage(
                            'Thank you! Your message was sent successfully.',
                            true,
                        );
                    } else {
                        this.responseMessage(
                            "We're sorry, there was an error sending your message.  Please try again at a later time.",
                        );
                    }
                })
                .catch((err) => {
                    console.error(err);
                    this.responseMessage(
                        "We're sorry, there was an error sending your message.  Please try again at a later time.",
                    );
                });
        },
        setActiveProduct(product) {
            if (this.products.filter((p) => p.name.toLowerCase() === product).length < 1) {
                product = 'auto';
            }
            this.activeProduct = product;
            this.quoteForm.lob = this.products.find((p) => p.name.toLowerCase() === product).code;
            if (this.$route.params.product !== product) {
                this.$router.push({
                    name: 'quote',
                    params: { product: product },
                    query: this.$route.query,
                });
            }
        },
    },
};
</script>
<style lang="scss" scoped>
.mobile-qlp-business-brochure-image {
    display: block;
    width: 100%;
    max-width: 500px;
    border-radius: 2rem;
    margin: 2rem auto 0 auto;

    @include media('>=md') {
        display: none;
    }
}

.desktop-qlp-business-brochure-image {
    width: auto;
    border-radius: 2rem;
    margin: 4rem 0 2rem 0;
    display: block;

    @include media('<md') {
        display: none;
    }
}

select {
    background: white;
    font-size: 18px;
    padding: 10px;
    border-radius: 6px;
    color: black;
    border: none;
}

select:hover,
select:focus-visible {
    cursor: pointer;
}

.quote-page {
    padding: 0 4rem 4rem 4rem;
    margin: auto;
    max-width: 1200px;
    text-align: left;

    @include media('<md') {
        padding: 0 0 4rem 0;
    }

    h1 {
        font-size: 4rem;
    }
    .banner {
        width: 100%;
        aspect-ratio: 3.5;
        object-fit: cover;
    }

    .blocks {
        display: grid;
        grid-template-columns: 1fr 1fr;
        align-items: start;
        gap: 4rem;
        margin: 4rem 2rem 0 2rem;

        .block {
            width: 100%;
        }

        @include media('<md') {
            grid-template-columns: 1fr;
        }
    }

    .quoter-box {
        width: 100%;
        border-radius: 16px;
        background-color: $green;
        color: white;
        display: flex;
        padding: 3rem;
        flex-direction: column;
        margin: 0px auto;
        max-width: 500px;
        container-type: inline-size;

        input,
        select {
            &.hasError {
                border: 1px solid red;
            }
        }

        h2 {
            color: white;
            margin-bottom: 0;
        }

        .quote-form {
            display: grid;
            margin-top: 1rem;

            @container (min-width: 350px) {
                grid-template-columns: 1fr 1fr;
                gap: 16px;
            }
        }
    }

    .profile {
        container-type: inline-size;
        max-width: 600px;
        margin: 0px auto;

        .agent-info {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 2rem;

            @container (min-width: 450px) {
                align-items: flex-start;
                flex-direction: row;
            }
            img {
                width: 200px;
                border-radius: 12px;
            }
            .contact {
                display: grid;
                place-items: center;
                text-align: center;

                @container (min-width: 450px) {
                    place-items: start;
                    text-align: start;
                }

                a {
                    color: $green;
                    font-size: 22px;
                    text-decoration: none;
                    &.icon-and-text {
                        display: flex;
                        align-items: center;
                        gap: 0.5rem;
                        font-size: 1.9rem;
                    }
                }
                h1 {
                    color: $black;
                    font-size: 2.5rem;
                    font-weight: bold;
                    margin: 0;
                    margin-top: 12px;
                }
                p {
                    font-size: 18px;

                    span {
                        display: block;
                        margin-bottom: 20px;
                    }
                }
            }
        }
        .contact-form {
            display: flex;
            flex-direction: row;
            flex-wrap: wrap;
            justify-content: space-between;

            input,
            textarea {
                border: 1px solid $green;
                font-size: 18px;
                padding: 10px;
                border-radius: 6px;
                &.hasError {
                    border: 1px solid red;
                }
            }
            button {
                margin: 1rem auto;
            }
            h4 {
                width: 100%;
                color: $green;
                font-size: 24px;
                font-weight: bold;
                margin: 20px 0 5px 0;
            }
            .name-and-phemail {
                width: 100%;
                display: flex;
                gap: 1rem;
            }
            .inputWrapper {
                width: 100%;
                input {
                    width: 100%;
                    border-radius: 6px;
                }
            }
            textarea {
                width: 100%;
                border-radius: 6px;
                margin-top: 1rem;
            }
        }
    }

    input,
    textarea {
        font-size: 18px;
        padding: 10px;
        border-radius: 6px;
    }
}
</style>
